import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { qk } from '../../../lib/queryKeys';
import { useAuth } from '../../../lib/authContext';
import {
  archiveBenchOrgPlan,
  getBenchMandatorySet,
  getBenchPlan,
  isPlansDisabled,
  planErrorCode,
  planErrorStatus,
  saveBenchOrgPlanVersion,
} from '../../../lib/benchPlans';
import { PlanBuilder } from '../../../components/bench/PlanBuilder';
import { draftChanged, draftFromPlan, draftToPayload, ownerLabel, planErrorMessage, type PlanDraft } from '../../../components/bench/planModel';

/**
 * SIL-2246 U1. One plan: area cards. An org admin edits an own org plan (each save is a new version) or archives it; a member,
 * and every SiliconX template, is read-only. The mandatory rows are the LIVE set, not a copy.
 */
export const Route = createFileRoute('/bench/plans/$planId')({
  component: BenchPlanRoute,
});

function BenchPlanRoute() {
  const { planId } = Route.useParams();
  return <BenchPlanView planId={planId} />;
}

export function BenchPlanView({ planId }: { planId: string }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plan = useQuery({ queryKey: qk.bench.plan(planId), queryFn: () => getBenchPlan(planId), retry: false });
  const mandatory = useQuery({ queryKey: qk.bench.mandatorySet(), queryFn: getBenchMandatorySet, retry: false, staleTime: 60_000 });
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const saved = useMemo(() => (plan.data ? draftFromPlan(plan.data) : null), [plan.data]);
  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);

  const editable = isAdmin && plan.data?.origin === 'org';
  const changed = draft !== null && saved !== null && draftChanged(draft, saved);

  const save = useMutation({
    mutationFn: () => saveBenchOrgPlanVersion(planId, draftToPayload(draft!)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.bench.plansAll() });
    },
  });
  const archive = useMutation({
    mutationFn: () => archiveBenchOrgPlan(planId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.bench.plansAll() });
      void navigate({ to: '/bench/plans' });
    },
  });

  if (plan.isError && isPlansDisabled(plan.error)) {
    return (
      <div className="space-y-6">
        <PageHeader title="Bench plan" />
        <InsightBanner tone="neutral" lead="Plans are not available yet." />
      </div>
    );
  }
  const notFound = plan.isError && planErrorStatus(plan.error) === 404;

  const errCode = (save.isError ? planErrorCode(save.error) : undefined) ?? (archive.isError ? planErrorCode(archive.error) : undefined);
  const errStatus = save.isError ? planErrorStatus(save.error) : archive.isError ? planErrorStatus(archive.error) : undefined;
  const actionErr = save.isError || archive.isError ? (planErrorMessage(errCode, errStatus) ?? 'That did not work. Try again.') : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={plan.data?.name ?? 'Bench plan'}
        subtitle={
          plan.data ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusPill tone={plan.data.origin === 'siliconx' ? 'neutral' : 'green'}>{ownerLabel(plan.data.origin)}</StatusPill>
              <span>v{plan.data.version}</span>
              {plan.data.updateAvailable && <StatusPill tone="amber">Update available</StatusPill>}
            </span>
          ) : undefined
        }
        actions={<Link to="/bench/plans" className="text-sm text-muted-foreground hover:underline">Back to plans</Link>}
      />
      {plan.isPending && <LoadingShell rows={6} />}
      {notFound && <InsightBanner tone="neutral" lead="That plan was not found." />}
      {plan.isError && !notFound && <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />}

      {plan.data?.updateAvailable && (
        <InsightBanner tone="amber" lead="SiliconX published a newer version of the template this plan was copied from.">
          Your plan keeps the tests you saved. To adopt the new template, copy it to a new plan from the plans list and re-add your own tests.
        </InsightBanner>
      )}
      {plan.data && !editable && (
        <InsightBanner tone="neutral" lead={plan.data.origin === 'siliconx' ? 'SiliconX template. Read only.' : 'Only an org admin can edit plans.'} />
      )}

      {plan.data && draft && (
        <>
          <PlanBuilder draft={draft} mandatory={mandatory.data ?? null} readOnly={!editable || save.isPending} onChange={setDraft} />
          {actionErr && <p role="alert" className="text-sm text-status-red">{actionErr}</p>}
          {save.isSuccess && !changed && <p className="text-sm text-status-green">Saved as version {save.data.version}.</p>}
          {editable && (
            <Card className="flex flex-wrap items-center gap-3">
              <Button onClick={() => save.mutate()} disabled={!changed || save.isPending || archive.isPending}>
                Save new version
              </Button>
              {confirmArchive ? (
                <>
                  <span className="text-sm">Archive this plan? It can no longer be selected at approval.</span>
                  <Button variant="destructive" onClick={() => archive.mutate()} disabled={archive.isPending}>Archive plan</Button>
                  <Button variant="secondary" onClick={() => setConfirmArchive(false)} disabled={archive.isPending}>Cancel</Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmArchive(true)} disabled={save.isPending}>Archive</Button>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
